import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import apiClient from "#/redux/api/ApiClient.js";
import { handleLoadingAndErrorState } from "#/redux/util/reduxUtils.js";
import { getErrorMessage } from "#/utils/APIUtils.js";

const baseUrl = "/api/dog/analize/labVzorec";

/**
 * Maps LabVzorecVo to LabVzorecDto for API requests
 * @param {LabVzorecVo} vo - The LabVzorecVo object
 * @returns {LabVzorecDto} The mapped LabVzorecDto object
 */
export const mapLabVzorecVoToDto = (vo) => {
  return {
    id: vo.id,
    subjektId: vo.subjektId,
    zivalId: vo.zivalId,
    stevilkaVzorca: vo.stevilkaVzorca,
    datumOdvzema: vo.datumOdvzema,
    datumPrejema: vo.datumPrejema,
    vrstaVzorca: vo.vrstaVzorca,
    opis: vo.opis,
    odvzel: vo.odvzelId,
    zaporedniOdvzem: vo.zaporedniOdvzem,
    opombe: vo.opombe,
    posiljkaId: vo.posiljkaId,
    datumPrejemaIzvida: vo.datumPrejemaIzvida,
    datumGenotipizacije: vo.datumGenotipizacije,
    uspesnostGenotipizacije: vo.uspesnostGenotipizacije,
    oznakaCipa: vo.oznakaCipa,
    version: vo.version,
  };
};

// ==================== TAB-SPECIFIC THUNKS ====================

/**
 * Fetch collected samples (posiljkaId IS NULL)
 * Tab: "Odvzeti vzorci"
 */
export const fetchCollectedSamplesPage = createAsyncThunk(
  "labVzorec/fetchCollectedPage",
  async ({ page, filter, sort, hasStatus = false }, { rejectWithValue }) => {
    try {
      const existingFilters = filter?.toJSON?.() || [];
      const finalFilter = new ApiFilter();
      finalFilter.rootFilters.push(...existingFilters);

      if (!hasStatus) {
        finalFilter.isNull("posiljkaId");
      } else {
        finalFilter.or((orGroup) => {
          orGroup.isNull("statusVzorca");
          orGroup.equal("statusVzorca", "SHRANJEN");
        });
      }

      const response = await apiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        finalFilter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Fetch shipped samples (posiljkaId IS NOT NULL)
 * Tab: "Posiljke vzorcev v laboratorij"
 */
export const fetchShippedSamplesPage = createAsyncThunk(
  "labVzorec/fetchShippedPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const existingFilters = filter?.toJSON?.() || [];
      const finalFilter = new ApiFilter();
      finalFilter.rootFilters.push(...existingFilters);
      finalFilter.isNotNull("posiljkaId").isNull("datumPrejemaIzvida");

      const response = await apiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        finalFilter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Fetch samples with results received (datumPrejemaIzvida IS NOT NULL)
 * Tab: "Posiljke vzorcev v lab. s prejetimi izvidi"
 */
export const fetchResultsReceivedSamplesPage = createAsyncThunk(
  "labVzorec/fetchResultsReceivedPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const existingFilters = filter?.toJSON?.() || [];
      const finalFilter = new ApiFilter();
      finalFilter.rootFilters.push(...existingFilters);
      finalFilter.isNotNull("datumPrejemaIzvida");

      const response = await apiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        finalFilter,
        sort,
        false
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Fetch single lab sample by ID
 */
export const fetchLabVzorecById = createAsyncThunk(
  "labVzorec/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Create new lab sample
 */
export const createLabVzorec = createAsyncThunk(
  "labVzorec/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(baseUrl, dto);
      return response.data?.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Update existing lab sample
 */
export const updateLabVzorec = createAsyncThunk(
  "labVzorec/update",
  async ({ id, dto }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${baseUrl}/${id}`, dto);
      return response.data?.output;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Delete lab sample
 */
export const deleteLabVzorec = createAsyncThunk(
  "labVzorec/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`${baseUrl}/${id}`);
      return { id, response: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Generate next sample number (auto-incrementing integer starting from 10000)
 */
export const generateStevilkaVzorca = createAsyncThunk(
  "labVzorec/generateStevilka",
  async (_, { rejectWithValue }) => {
    try {
      // Start with hardcoded base value
      const baseValue = 10000;

      // Find the highest existing sample number
      const response = await apiClient.get(
        `${baseUrl}/page`,
        undefined,
        { page: 0, size: 1 },
        undefined,
        "sort=stevilkaVzorca,DESC"
      );

      let nextNumber = baseValue;
      const content = response.data?.content || [];

      if (content.length > 0) {
        // Extract number from last sample (e.g., "10005" -> 10005)
        const lastStevilka = content[0].stevilkaVzorca;
        const lastNumber = parseInt(lastStevilka, 10);

        // Only increment if the last number is valid and >= baseValue
        if (!Number.isNaN(lastNumber) && lastNumber >= baseValue) {
          nextNumber = lastNumber + 1;
        }
      }

      // Return as string to maintain consistency with existing API
      return nextNumber.toString();
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * Get next zaporedni odvzem for animal
 */
export const getNextZaporedniOdvzem = createAsyncThunk(
  "labVzorec/nextZaporedniOdvzem",
  async (zivalId, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().equal("zivalId", zivalId);

      const response = await apiClient.get(
        `${baseUrl}/page`,
        undefined,
        { page: 0, size: 1 },
        filter,
        "zaporedniOdvzem,desc",
        false
      );

      const content = response.data?.content || [];
      if (content.length > 0 && content[0].zaporedniOdvzem != null) {
        return content[0].zaporedniOdvzem + 1;
      }
      return 1;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

// ==================== THUNKS ARRAY FOR isAnyOf ====================

const thunks = [
  fetchLabVzorecById,
  createLabVzorec,
  updateLabVzorec,
  deleteLabVzorec,
  generateStevilkaVzorca,
  getNextZaporedniOdvzem,
];

// ==================== SLICE ====================

const labVzorecSlice = createSlice({
  name: "labVzorec",
  initialState: {
    // Tab-specific pages
    collectedPage: {},
    shippedPage: {},
    resultsReceivedPage: {},
    // Single item
    current: null,
    byId: {},
    // Auto-generation
    generatedStevilka: null,
    nextZaporedniOdvzem: null,
    // Loading states
    isLoading: false,
    isLoadingCollected: false,
    isLoadingShipped: false,
    isLoadingResultsReceived: false,
    error: null,
  },
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
    },
    clearGeneratedStevilka: (state) => {
      state.generatedStevilka = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Collected samples page
      .addCase(fetchCollectedSamplesPage.pending, (state) => {
        state.isLoadingCollected = true;
      })
      .addCase(fetchCollectedSamplesPage.fulfilled, (state, action) => {
        state.collectedPage = action.payload;
        state.isLoadingCollected = false;
      })
      .addCase(fetchCollectedSamplesPage.rejected, (state) => {
        state.isLoadingCollected = false;
      })
      // Shipped samples page
      .addCase(fetchShippedSamplesPage.pending, (state) => {
        state.isLoadingShipped = true;
      })
      .addCase(fetchShippedSamplesPage.fulfilled, (state, action) => {
        state.shippedPage = action.payload;
        state.isLoadingShipped = false;
      })
      .addCase(fetchShippedSamplesPage.rejected, (state) => {
        state.isLoadingShipped = false;
      })
      // Results received samples page
      .addCase(fetchResultsReceivedSamplesPage.pending, (state) => {
        state.isLoadingResultsReceived = true;
      })
      .addCase(fetchResultsReceivedSamplesPage.fulfilled, (state, action) => {
        state.resultsReceivedPage = action.payload;
        state.isLoadingResultsReceived = false;
      })
      .addCase(fetchResultsReceivedSamplesPage.rejected, (state) => {
        state.isLoadingResultsReceived = false;
      })
      // Single fetch
      .addCase(fetchLabVzorecById.fulfilled, (state, action) => {
        state.current = action.payload;
        if (action.payload?.id) {
          state.byId[action.payload.id] = action.payload;
        }
      })
      // Create
      .addCase(createLabVzorec.fulfilled, (state, action) => {
        state.current = action.payload;
        if (action.payload?.id) {
          state.byId[action.payload.id] = action.payload;
        }
      })
      // Update
      .addCase(updateLabVzorec.fulfilled, (state, action) => {
        state.current = action.payload;
        if (action.payload?.id) {
          state.byId[action.payload.id] = action.payload;
        }
      })
      // Delete
      .addCase(deleteLabVzorec.fulfilled, (state, action) => {
        const { id } = action.payload;
        delete state.byId[id];
        if (state.current?.id === id) {
          state.current = null;
        }
      })
      // Generate stevilka
      .addCase(generateStevilkaVzorca.fulfilled, (state, action) => {
        state.generatedStevilka = action.payload;
      })
      // Next zaporedni odvzem
      .addCase(getNextZaporedniOdvzem.fulfilled, (state, action) => {
        state.nextZaporedniOdvzem = action.payload;
      });

    handleLoadingAndErrorState(builder, thunks);
  },
});

export const { clearCurrent, clearGeneratedStevilka } = labVzorecSlice.actions;

export default labVzorecSlice.reducer;
